import { useState, useCallback, useEffect } from "react";
import { apiGet, apiPost, apiPut, apiDelete, ApiResponse } from "../services/api.service";

export interface PrmAssistantData {
	assistant_id: number;
	extra_id: number;
	first_name: string;
	last_name: string;
	date_of_birth: string;
	city_of_birth: string;
	country_of_birth: number;
	geburtsname: string;
	street: string;
	postcode: string;
	city: string;
	iban: string;
	bic: string;
	idnr: string;
	sozialversicherung: string;
	insurance_type: string;
	insurance_company: string;
	nationality: number;
	id_number: string;
	main_occupation_id: number;
	assistant_profile_completed: boolean;
	check_in: string;
	check_out: string;
}

export function usePrmAssistant(extraId: number | undefined) {
	const [assistant, setAssistant] = useState<PrmAssistantData | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchAssistant = useCallback(async () => {
		if (!extraId) return;

		setLoading(true);
		setError(null);

		try {
			const response: ApiResponse<PrmAssistantData> = await apiGet(
				`/prm-assistants/by-extra/${extraId}`,
				undefined,
				{ notification: { enabled: false, showError: false } }
			);

			if (response.success && response.data) {
				setAssistant(response.data);
			} else {
				setAssistant(null);
			}
		} catch (err: any) {
			// 404 means no assistant exists yet, which is not an error
			if (err?.status === 404) {
				setAssistant(null);
			} else {
				setError(err?.message || "Fehler beim Laden des Assistenten");
			}
		} finally {
			setLoading(false);
		}
	}, [extraId]);

	useEffect(() => {
		fetchAssistant();
	}, [fetchAssistant]);

	const createAssistant = useCallback(
		async (data: Partial<PrmAssistantData>) => {
			const response: ApiResponse<PrmAssistantData> = await apiPost(
				"/prm-assistants",
				{ extra_id: extraId, ...data }
			);
			if (response.success && response.data) {
				setAssistant(response.data);
			}
			return response;
		},
		[extraId]
	);

	const updateAssistant = useCallback(
		async (assistantId: number, data: Partial<PrmAssistantData>) => {
			const response: ApiResponse<PrmAssistantData> = await apiPut(
				`/prm-assistants/${assistantId}`,
				data
			);
			if (response.success && response.data) {
				setAssistant(response.data);
			}
			return response;
		},
		[]
	);

	const deleteAssistant = useCallback(
		async (assistantId: number) => {
			const response: ApiResponse<any> = await apiDelete(
				`/prm-assistants/${assistantId}`
			);
			if (response.success) {
				setAssistant(null);
			}
			return response;
		},
		[]
	);

	const refetch = useCallback(() => {
		return fetchAssistant();
	}, [fetchAssistant]);

	return {
		assistant,
		loading,
		error,
		createAssistant,
		updateAssistant,
		deleteAssistant,
		refetch,
	};
}
